import { AXIOME_REST, msgExecuteContract, toMicro, type CosmosMsg } from "./types.js";

export const INDEXER_URL = "https://api-idx.axiomechain.pro";

export type IndexerToken = {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logo?: string;
  type: string;
  reserve?: string;
};

export type IndexerPool = {
  contractAddress: string;
  lpContractAddress: string;
  apr: number;
  liq: string;
  /** USD TVL in micro-units (1e6) when present */
  capUsd?: string;
  capAxm?: string;
  vol24: string;
  vol168: string;
  feesFrom24: string;
  feesTo24: string;
  feesNorm: number;
  price: number;
  priceAxm: number;
  priceUsd: number;
  poolFeePercent: string;
  creatorFeePercent: string;
  pairInfoToken1Reserve: string;
  pairInfoToken2Reserve: string;
  token1: IndexerToken;
  token2: IndexerToken;
  chg24: number;
};

export type EnrichedMarket = {
  symbol: string;
  /** Unique id = pool contract */
  id: string;
  pool: string;
  token: string;
  tokenDecimals: number;
  tokenLogo?: string;
  tokenName: string;
  apr: number;
  /** Counter-LP share of APR (90% of pool LP income side) */
  effectiveApr: number;
  tvlAxm: number;
  /** Approximate USD TVL from indexer */
  tvlUsd: number;
  vol24Axm: number;
  fees24Axm: number;
  priceTokenInAxm: number;
  reserveAxm: string;
  reserveToken: string;
  /** LP token total supply from indexer pair when available */
  lpTokenSupply?: string;
  poolFeePercent: number;
  inventory?: string;
  active?: boolean;
  /** On-chain vault total_lp when reconciled */
  vaultTotalLp?: string;
};

function microToNum(v: string | undefined, decimals = 6): number {
  if (!v) return 0;
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n / 10 ** decimals;
}

export async function fetchIndexerPools(opts?: {
  perPage?: number;
  indexer?: string;
}): Promise<IndexerPool[]> {
  const base = (opts?.indexer ?? INDEXER_URL).replace(/\/$/, "");
  const perPage = Math.min(Math.max(opts?.perPage ?? 100, 20), 200);
  const all: IndexerPool[] = [];
  let page = 1;
  let total = Infinity;

  while (all.length < total && page <= 50) {
    const url = `${base}/swap/pools/paginated?page=${page}&perPage=${perPage}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`indexer pools failed: ${res.status}`);
    const body = (await res.json()) as {
      items?: IndexerPool[];
      total?: number;
      page?: number;
      perPage?: number;
    };
    const items = body.items ?? [];
    total = Number(body.total ?? all.length + items.length);
    all.push(...items);
    if (items.length === 0) break;
    if (items.length < perPage) break;
    page += 1;
  }

  // Deduplicate by pool contract
  const seen = new Set<string>();
  const unique: IndexerPool[] = [];
  for (const p of all) {
    const id = p.contractAddress;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    unique.push(p);
  }
  return unique;
}

/** Only AXM quote pairs suitable for counter-LP */
export function enrichAxmPools(pools: IndexerPool[]): EnrichedMarket[] {
  const out: EnrichedMarket[] = [];
  const symbolCount = new Map<string, number>();

  for (const p of pools) {
    const t1 = p.token1;
    const t2 = p.token2;
    if (!t1 || !t2) continue;

    const isAxm = (t: IndexerToken) => {
      const sym = (t.symbol || "").toUpperCase().trim();
      const addr = (t.address || "").toLowerCase();
      return sym === "AXM" || addr === "axm" || addr === "uaxm";
    };

    const axmIs1 = isAxm(t1);
    const axmIs2 = isAxm(t2);
    // Need exactly one side AXM
    if (axmIs1 === axmIs2) continue;

    const base = axmIs1 ? t2 : t1;
    const axm = axmIs1 ? t1 : t2;
    if (!(base.address || "").startsWith("axm1")) continue;

    const reserveAxm = axmIs1 ? p.pairInfoToken1Reserve : p.pairInfoToken2Reserve;
    const reserveToken = axmIs1 ? p.pairInfoToken2Reserve : p.pairInfoToken1Reserve;
    const apr = Number(p.apr) || 0;
    const effectiveApr = apr * 0.9;
    let symbol = (base.symbol || "TOKEN").toUpperCase().trim() || "TOKEN";
    const n = (symbolCount.get(symbol) ?? 0) + 1;
    symbolCount.set(symbol, n);
    if (n > 1) {
      symbol = `${symbol}_${base.address.slice(-4).toUpperCase()}`;
    }

    const tvlAxm = microToNum(p.liq, 6);
    const tvlUsd = microToNum(p.capUsd, 6);

    out.push({
      id: p.contractAddress,
      symbol,
      pool: p.contractAddress,
      token: base.address,
      tokenDecimals: base.decimals ?? 6,
      tokenLogo: base.logo?.startsWith("http") ? base.logo : undefined,
      tokenName: base.name || base.symbol,
      apr,
      effectiveApr,
      tvlAxm,
      tvlUsd,
      vol24Axm: microToNum(p.vol24, 6),
      fees24Axm:
        microToNum(p.feesFrom24, 6) +
        microToNum(p.feesTo24, base.decimals ?? 6) * (p.priceAxm || p.price || 0),
      priceTokenInAxm: p.priceAxm || p.price || 0,
      reserveAxm: reserveAxm || axm.reserve || "0",
      reserveToken: reserveToken || base.reserve || "0",
      poolFeePercent: Number(p.poolFeePercent) || 0,
    });
  }
  out.sort((a, b) => b.tvlUsd - a.tvlUsd || b.effectiveApr - a.effectiveApr);
  return out;
}

/** Default listing threshold for Counter LP markets */
export const MIN_MARKET_TVL_USD = 100;

export function filterMarketsByMinTvlUsd(
  markets: EnrichedMarket[],
  minUsd: number = MIN_MARKET_TVL_USD,
): EnrichedMarket[] {
  return markets.filter((m) => m.tvlUsd >= minUsd);
}

/** Pin featured symbols to the top (stable order), keep the rest as-is. */
export function pinMarketsFirst(
  markets: EnrichedMarket[],
  pinnedSymbols: string[] = ["KEPY", "AXMB", "SIMBA"],
): EnrichedMarket[] {
  const pinnedUpper = pinnedSymbols.map((s) => s.toUpperCase());
  const rank = new Map(pinnedUpper.map((s, i) => [s, i]));
  const pinned: EnrichedMarket[] = [];
  const rest: EnrichedMarket[] = [];
  for (const m of markets) {
    if (rank.has(m.symbol.toUpperCase())) pinned.push(m);
    else rest.push(m);
  }
  pinned.sort(
    (a, b) =>
      (rank.get(a.symbol.toUpperCase()) ?? 999) -
      (rank.get(b.symbol.toUpperCase()) ?? 999),
  );
  return [...pinned, ...rest];
}

/** Build RegisterMarket msgs for indexer markets (admin batch). */
export function buildRegisterMarketsBatch(params: {
  sender: string;
  vault: string;
  markets: EnrichedMarket[];
}): CosmosMsg[] {
  return params.markets.map((m) =>
    buildRegisterMarketMsg({
      sender: params.sender,
      vault: params.vault,
      symbol: m.symbol,
      pool: m.pool,
      token: m.token,
      tokenDecimals: m.tokenDecimals,
    }),
  );
}

export type PositionPnl = {
  symbol: string;
  /** Principal deposited */
  depositedAxm: number;
  depositedToken: number;
  /** Estimated current share of pool */
  currentAxm: number;
  currentToken: number;
  /** Deltas */
  pnlAxm: number;
  pnlToken: number;
  pnlAxmPct: number;
  /** Value in AXM terms */
  depositedValueAxm: number;
  currentValueAxm: number;
  pnlValueAxm: number;
  pnlValuePct: number;
  priceTokenInAxm: number;
};

/**
 * Estimate current AXM claimable value for a Counter-LP position.
 *
 * Prefer `estimatedWithdrawAxmMicro` from on-chain `preview_withdraw` when available.
 * Fallback share math MUST use the **pool** LP supply (not vault total_lp) with pool AXM reserve:
 *   userLp / poolLpSupply * reserveAxm
 * Using vault total_lp as denominator with full-pool reserves hugely overstates PnL.
 */
export function calcPositionPnl(input: {
  symbol: string;
  axmDepositedMicro: string;
  tokenMatchedMicro: string;
  lpAmountMicro: string;
  pendingPoolMicro?: string;
  pendingCommunityMicro?: string;
  pendingTokenMicro?: string;
  tokenDecimals: number;
  priceTokenInAxm: number;
  reserveAxmMicro?: string;
  reserveTokenMicro?: string;
  /** @deprecated Misnamed — was often vault total_lp. Prefer poolLpSupplyMicro. */
  totalLpMicro?: string;
  /** Pool-wide LP token supply (from pair `info.lp_token_supply`). */
  poolLpSupplyMicro?: string;
  /** On-chain preview_withdraw.estimated_axm (micro). */
  estimatedWithdrawAxmMicro?: string;
  depositorPoolShare?: number;
}): PositionPnl {
  const depAxm = microToNum(input.axmDepositedMicro, 6);
  const depTok = microToNum(input.tokenMatchedMicro, input.tokenDecimals);
  const price = input.priceTokenInAxm || 0;
  const userShare =
    input.depositorPoolShare && input.depositorPoolShare > 0 && input.depositorPoolShare <= 1
      ? input.depositorPoolShare
      : 0.9;

  const axmRewards =
    microToNum(input.pendingPoolMicro || "0", 6) +
    microToNum(input.pendingCommunityMicro || "0", 6);
  const tokenRewards =
    microToNum(input.pendingTokenMicro || "0", input.tokenDecimals) * price;

  const lp = Number(input.lpAmountMicro || "0");
  const poolLp = Number(input.poolLpSupplyMicro || input.totalLpMicro || "0");
  const rAxm = Number(input.reserveAxmMicro || "0");
  const previewAxm = Number(input.estimatedWithdrawAxmMicro || "0");

  let lpAxmOut = depAxm;
  if (previewAxm > 0) {
    lpAxmOut = previewAxm / 1e6;
  } else if (lp > 0 && poolLp > 0 && rAxm > 0 && poolLp >= lp) {
    // user share of the whole pair, not of vault inventory
    lpAxmOut = (lp / poolLp) * (rAxm / 1e6);
  }

  // Approximate withdraw payout after platform cut on positive excess vs deposit
  // (preview_withdraw already applies cuts — don't double-apply)
  let withdrawAxm = lpAxmOut;
  if (!previewAxm && withdrawAxm > depAxm) {
    const profit = withdrawAxm - depAxm;
    withdrawAxm = depAxm + profit * userShare;
  }

  const curAxm = withdrawAxm + axmRewards;
  const currentValueAxm = curAxm + tokenRewards;
  const depositedValueAxm = depAxm;
  const pnlAxm = curAxm - depAxm;
  const pnlValueAxm = currentValueAxm - depositedValueAxm;

  return {
    symbol: input.symbol,
    depositedAxm: depAxm,
    depositedToken: depTok,
    currentAxm: curAxm,
    currentToken: microToNum(input.pendingTokenMicro || "0", input.tokenDecimals),
    pnlAxm,
    pnlToken: 0,
    pnlAxmPct: depAxm > 0 ? (pnlAxm / depAxm) * 100 : 0,
    depositedValueAxm,
    currentValueAxm,
    pnlValueAxm,
    pnlValuePct: depositedValueAxm > 0 ? (pnlValueAxm / depositedValueAxm) * 100 : 0,
    priceTokenInAxm: price,
  };
}

export function formatCompact(n: number, digits = 2): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(digits)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(digits)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(digits)}K`;
  if (abs >= 100) return `${sign}${abs.toFixed(0)}`;
  if (abs >= 1) return `${sign}${abs.toFixed(digits)}`;
  if (abs >= 0.01) return `${sign}${abs.toFixed(3)}`;
  return `${sign}${abs.toFixed(4)}`;
}

export function formatSigned(n: number, digits = 2): string {
  const base = formatCompact(Math.abs(n), digits);
  if (n > 0) return `+${base}`;
  if (n < 0) return `-${base}`;
  return base;
}

/** Permissionless: skim pool fee growth into reward budget */
export function buildHarvestMsg(params: {
  sender: string;
  vault: string;
  symbol: string;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: { harvest: { symbol: params.symbol } },
  });
}

/** Anyone can fund LP Bonus / community rewards with AXM */
export function buildFundCommunityRewardsMsg(params: {
  sender: string;
  vault: string;
  symbol: string;
  amountHuman: string;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: { fund_community_rewards: { symbol: params.symbol } },
    funds: [{ denom: "uaxm", amount: toMicro(params.amountHuman) }],
  });
}

export function buildRegisterMarketMsg(params: {
  sender: string;
  vault: string;
  symbol: string;
  pool: string;
  token: string;
  tokenDecimals: number;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: {
      register_market: {
        symbol: params.symbol,
        pool: params.pool,
        token: params.token,
        token_decimals: params.tokenDecimals,
      },
    },
  });
}

/** Admin: withdraw only accumulated platform treasury cut */
export function buildWithdrawTreasuryMsg(params: {
  sender: string;
  vault: string;
  amountHuman?: string;
  recipient?: string;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: {
      withdraw_treasury: {
        amount: params.amountHuman ? toMicro(params.amountHuman) : null,
        recipient: params.recipient ?? null,
      },
    },
  });
}

export function buildSetMarketStatusMsg(params: {
  sender: string;
  vault: string;
  symbol: string;
  active: boolean;
}): CosmosMsg {
  return msgExecuteContract({
    sender: params.sender,
    contract: params.vault,
    msg: {
      set_market_status: { symbol: params.symbol, active: params.active },
    },
  });
}

function btoaJson(obj: unknown): string {
  const json = JSON.stringify(obj);
  return typeof btoa === "function"
    ? btoa(json)
    : Buffer.from(json, "utf8").toString("base64");
}

export async function queryPoolInfo(
  pool: string,
  rest: string = AXIOME_REST,
): Promise<{
  token1_reserve: string;
  token2_reserve: string;
  lp_token_supply: string;
  lp_token_address: string;
} | null> {
  try {
    const q = btoaJson({ info: {} });
    const url = `${rest.replace(/\/$/, "")}/cosmwasm/wasm/v1/contract/${pool}/smart/${q}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const body = await res.json();
    return body.data ?? body;
  } catch {
    return null;
  }
}
